import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { BsLightningFill } from 'react-icons/bs';
import { FaFileExcel, FaMoneyCheckAlt } from 'react-icons/fa';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import { IIccoBreakdownSearchCriteria } from '../AccountsReceivableServiceContext';

interface IIccoBreakdownSearchProps {
    runQuery: (criteria: IIccoBreakdownSearchCriteria) => void;
    onExcelReport: (criteria: IIccoBreakdownSearchCriteria) => void;
    onMultiOfficeChecks: () => void;
}

// Matches the Type poplist's 3 literal list entries (confirmed via the .fmt's own static LOV text) -
// the exact same labels/value mapping already used for revenue.fmt's own "Report Type" field
// (WIP Not Applied -> STD, WIP Applied -> WIP, WIP Only -> WIPONLY), which lines up with
// invoice_office_breakdown's own rpt_type union labels ('STD'/'WIP'/'WIPONLY'). Confirmed via the
// .fmt trace that QUERY_DATA never reads :control.type at all - it only feeds the deferred Excel
// export dispatcher (XL_Icco_Reports) - so this field is carried for visual fidelity with the real
// screen but doesn't affect the query yet.
const TYPE_OPTIONS = [
    { value: 'STD', label: 'WIP Not Applied' },
    { value: 'WIP', label: 'WIP Applied' },
    { value: 'WIPONLY', label: 'WIP Only' },
];

// Mirrors the Control block (canvas ICCO) - Beginning Date/Ending Date/Type + the green Query
// button (oid 39/oid 35's WHEN-LIST-CHANGED both just re-run Query_Data). Oracle's own guard
// ("A beginning and ending date must be entered...") is enforced here before calling runQuery.
const IccoBreakdownSearch = (props: IIccoBreakdownSearchProps) => {
    const { register, errors, getValues } = useForm();
    const [message, setMessage] = useState<string>('');

    const validateDates = (): IIccoBreakdownSearchCriteria | undefined => {
        const values = getValues();
        if (!values.begDate || !values.endDate) {
            setMessage('A beginning and ending date must be entered.');
            return undefined;
        }
        setMessage('');
        return { begDate: values.begDate, endDate: values.endDate, type: values.type || 'STD' };
    };

    const executeSearch = () => {
        const criteria = validateDates();
        if (criteria) {
            props.runQuery(criteria);
        }
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 42] - same beg/end date guard as Query, then opens the
    // Excel Report popup (Go_Block('Excel')).
    const executeExcelReport = () => {
        const criteria = validateDates();
        if (criteria) {
            props.onExcelReport(criteria);
        }
    };

    return (
        <Page style={{ justifyContent: 'center' }}>
            <Page.Section width={12}>
                <Panel backgroundColor="#D3D3D3">
                    <Panel panelTitle="Invoice-Contractor Pay Office Breakdown">
                        <form>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                <div style={{ width: '18%' }}>
                                    <Fieldset label="Beginning Date" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="begDate" type="date" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '18%', paddingLeft: '4px' }}>
                                    <Fieldset label="Ending Date" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="endDate" type="date" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '20%', paddingLeft: '4px' }}>
                                    <Fieldset label="Type" style={{ border: 0, padding: 0 }}>
                                        <RHFSelect
                                            name="type"
                                            options={TYPE_OPTIONS}
                                            defaultVal="STD"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                    <Button
                                        title="Get Report ID"
                                        className="blue-hover-button"
                                        text=""
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: 'solid 1px #6393C1', borderRadius: '8px', padding: 0,
                                            height: '3rem', width: '3rem',
                                        }}
                                        iconStyle={{ fill: '#f1f115', width: '2.2rem', height: '2.2rem' }}
                                        reactIcon={BsLightningFill}
                                        onClick={() => executeSearch()}
                                    />
                                    <Button
                                        title="Excel Report Options"
                                        className="blue-hover-button"
                                        text=""
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: 'solid 1px #6393C1', borderRadius: '8px', padding: 0,
                                            height: '3rem', width: '3rem', marginLeft: '1rem',
                                        }}
                                        iconStyle={{ fill: '#217346', width: '2.2rem', height: '2.2rem' }}
                                        reactIcon={FaFileExcel}
                                        onClick={() => executeExcelReport()}
                                    />
                                    {/* Mirrors WHEN-BUTTON-PRESSED [oid 50] - Go_Block('MO_EXCEL'), no date guard. */}
                                    <Button
                                        title="Multi-Office Checks"
                                        className="blue-hover-button"
                                        text=""
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: 'solid 1px #6393C1', borderRadius: '8px', padding: 0,
                                            height: '3rem', width: '3rem', marginLeft: '1rem',
                                        }}
                                        iconStyle={{ fill: '#4a6fa5', width: '2.2rem', height: '2.2rem' }}
                                        reactIcon={FaMoneyCheckAlt}
                                        onClick={() => props.onMultiOfficeChecks()}
                                    />
                                </div>
                            </div>
                            {!!message && (
                                <div style={{ color: 'red', textAlign: 'center', paddingBottom: '.5rem' }}>{message}</div>
                            )}
                        </form>
                    </Panel>
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default IccoBreakdownSearch;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/components/IccoBreakdownSearch.tsx